import React, { useRef } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import AppInput from "components/Input/AppInput";
import SelectField from "components/Select/SelectField";
import CustomDatePicker from "components/Input/CustomDatePicker";
import AppColor from "utilities/AppColor";
import { createClientDocument, updateDocument } from "store/slices/client";
import { CLIENT_DOCUMENT_CATEGORY, CLIENT_DOCUMENT_STATUS } from "config/AppConfig";
import moment from "moment";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
    gap: 8,
  },
  fileBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    marginBottom: "1rem",
  },
  fileName: {
    fontSize: 13,
    color: "#374151",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileLabel: {
    fontSize: 14,
    color: "#000",
    fontWeight: 500,
    marginBottom: "0.5rem",
  },
}));

const UploadDocumentModal = ({ open, onClose, clientId, onSuccess, initialData, initialFile }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const isEditMode = Boolean(initialData?.id);
  const [documentFile, setDocumentFile] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      if (initialFile) {
        setDocumentFile(initialFile);
      }
    } else {
      setDocumentFile(null);
    }
  }, [open, initialFile]);

  const categoryOptions = [
    { value: CLIENT_DOCUMENT_CATEGORY.NDIS, label: t("documentCategory.NDIS") },
    { value: CLIENT_DOCUMENT_CATEGORY.LEGAL, label: t("documentCategory.LEGAL") },
    { value: CLIENT_DOCUMENT_CATEGORY.CLINICAL, label: t("documentCategory.CLINICAL") },
    { value: CLIENT_DOCUMENT_CATEGORY.COMPLIANCE, label: t("documentCategory.COMPLIANCE") },
    { value: CLIENT_DOCUMENT_CATEGORY.CONSENT, label: t("documentCategory.CONSENT") },
  ];

  const statusCurrentOptions = [
    { value: CLIENT_DOCUMENT_STATUS.PENDING, label: t("documentStatus.PENDING") },
    { value: CLIENT_DOCUMENT_STATUS.CURRENT, label: t("documentStatus.CURRENT") },
    // { value: CLIENT_DOCUMENT_STATUS.REVIEW_DUE, label: t("documentStatus.REVIEW_DUE") },
    // { value: CLIENT_DOCUMENT_STATUS.OVERDUE, label: t("documentStatus.OVERDUE") },
    // { value: CLIENT_DOCUMENT_STATUS.SUPERSEDED, label: t("documentStatus.SUPERSEDED") },
  ];
  const statusSignedOptions = [
    { value: CLIENT_DOCUMENT_STATUS.PENDING, label: t("documentStatus.PENDING") },
    { value: CLIENT_DOCUMENT_STATUS.SIGNED, label: t("documentStatus.SIGNED") },
    // { value: CLIENT_DOCUMENT_STATUS.REVIEW_DUE, label: t("documentStatus.REVIEW_DUE") },
    // { value: CLIENT_DOCUMENT_STATUS.OVERDUE, label: t("documentStatus.OVERDUE") },
    // { value: CLIENT_DOCUMENT_STATUS.SUPERSEDED, label: t("documentStatus.SUPERSEDED") },
  ];

  const initialValues = {
    fileName: initialData?.fileName || "",
    category: initialData?.fileCategory || "",
    status: initialData?.status || CLIENT_DOCUMENT_STATUS.PENDING,
    reviewDueDate: initialData?.reviewDueDate ? moment(initialData.reviewDueDate).format("YYYY/MM/DD") : "",
  };

  const validationSchema = Yup.object().shape({
    fileName: Yup.string().trim().required(t("validation.required")).max(255, t("validation.max_length")),
    category: Yup.string().required(t("validation.required")),
    status: Yup.string().when([], {
      is: () => isEditMode,
      then: Yup.string().required(t("validation.required")),
    }),
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
      if (!allowedTypes.includes(file.type)) {
        return;
      }
      setDocumentFile(file);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    let result;

    if (isEditMode) {
      const payload = {
        id: initialData.id,
        fileName: values.fileName.trim(),
        category: values.category,
        status: values.status,
        reviewDueDate: values.reviewDueDate || null,
      };
      if (documentFile) {
        payload.documentFile = documentFile;
      }

      result = await dispatch(updateDocument(payload));
    } else {
      const payload = {
        clientId,
        fileName: values.fileName.trim(),
        category: values.category,
        reviewDueDate: values.reviewDueDate || null,
        documentFile: documentFile,
      };
      result = await dispatch(createClientDocument(payload));
    }

    setSubmitting(false);

    if (createClientDocument.fulfilled.match(result) || updateDocument.fulfilled.match(result)) {
      onSuccess && onSuccess();
      onClose();
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={isEditMode ? t("clientDocument.editDocument") : t("clientDocument.uploadDocument")}
      maxWidth="sm"
    >
      <Box>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting }) => {
            const statusOptions = values.category === CLIENT_DOCUMENT_CATEGORY.LEGAL || values.category === CLIENT_DOCUMENT_CATEGORY.CONSENT ? statusSignedOptions : statusCurrentOptions;
            return (
              <Form>
                <Typography className={classes.sectionTitle}>
                  {t("clientDocument.sectionDocument")}
                </Typography>

                <Box className={classes.fileBox}>
                  <i className="fas fa-file-pdf" style={{ fontSize: 18, color: "#EF4444" }} />
                  <Box className={classes.fileName}>
                    {documentFile
                      ? documentFile.name
                      : initialData?.fileAws3Link
                        ? t("clientDocument.documentUploaded")
                        : t("clientDocument.noDocument")
                    }
                  </Box>
                  <AppButton
                    label={documentFile || initialData?.fileAws3Link ? t("button.change") : t("button.upload")}
                    mode="outlined"
                    rounder
                    style={{ fontSize: 11, textTransform: "none", height: 28 }}
                    disabled={isSubmitting}
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = null;
                        fileInputRef.current.click();
                      }
                    }}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </Box>

                <Typography className={classes.sectionTitle}>
                  {t("clientDocument.sectionDetails")}
                </Typography>

                <div className="row">
                  <div className="col-sm-12">
                    <Field
                      component={AppInput}
                      name="fileName"
                      label="clientDocument.fileName"
                      placeholder="clientDocument.fileNamePlaceholder"
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="category"
                      label="clientDocument.category"
                      options={categoryOptions}
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={CustomDatePicker}
                      name="reviewDueDate"
                      label="clientDocument.reviewDueDate"
                      disabled={isSubmitting}
                      formatDateJob={true}
                    />
                  </div>
                </div>

                {isEditMode && !documentFile && (initialData.status !== CLIENT_DOCUMENT_STATUS.SIGNED && initialData.status !== CLIENT_DOCUMENT_STATUS.CURRENT) && (
                  <>
                    <Typography className={classes.sectionTitle}>
                      {t("clientDocument.sectionUpdate")}
                    </Typography>
                    <div className="row">
                      <div className="col-sm-12 col-md-6">
                        <Field
                          component={SelectField}
                          name="status"
                          label="clientDocument.status"
                          options={statusOptions}
                          disabled={isSubmitting}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* ── Actions ── */}
                <Box className={classes.control}>
                  <AppButton
                    label={t("button.cancel")}
                    onClick={onClose}
                    buttonColor="#F3F4F6"
                    rounder
                    style={{ color: "#111827" }}
                    disabled={isSubmitting}
                  />
                  <AppButton
                    label={t("button.confirm")}
                    rounder
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting || (!isEditMode && !documentFile)}
                    buttonColor={AppColor.mainColor}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </AppDialog>
  );
};

export default UploadDocumentModal;
